'use strict';
import React, { useEffect, useMemo, useState } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiCopy, FiEye, FiClipboard, FiCheckCircle, FiXCircle, FiPlay, FiCreditCard, FiPrinter, FiX, FiChevronLeft, FiChevronRight, FiExternalLink, FiEyeOff, FiChevronDown } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { OrderTemplatesAPI, OrdersAPI } from '../services/api';
import api from '../services/api';
import CreateTaskModal from './Tasks/components/Modals/CreateTaskModal/CreateTaskModal';
import HeaderDateFilters from './Tasks/components/Filters/HeaderDateFilters';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import CustomerDetail from './CustomerDetail';
import OrderDetail from './OrderDetail';
import PaymentModal from '../components/PaymentModal';
import { colors, matte, tableStyles, billingTableCSS } from '../styles/billingTableStyles';

const getCurrentFiscalYearRange = () => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const fyStartMonth = 3; // April (0-indexed)
  const fyStartYear = now.getMonth() >= fyStartMonth ? now.getFullYear() : now.getFullYear() - 1;
  const start = new Date(fyStartYear, fyStartMonth, 1, 0, 0, 0, 0);
  const end = new Date(fyStartYear + 1, 2, 31, 23, 59, 59, 999);
  return { start, end };
};

const styles = {
  page: {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${colors.stone50} 0%, ${colors.stone100} 100%)`,
    padding: '24px 32px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    flexWrap: 'wrap',
    gap: 12,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: colors.stone800,
    margin: 0,
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: 13,
    color: colors.stone500,
    margin: '2px 0 0',
  },
  headerActions: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  tabBar: {
    display: 'flex',
    gap: 4,
    marginBottom: 16,
    background: colors.stone100,
    padding: 4,
    borderRadius: 10,
    width: 'fit-content',
  },
  tab: {
    padding: '8px 16px',
    fontSize: 12,
    fontWeight: 500,
    color: colors.stone600,
    background: 'transparent',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  tabActive: {
    background: '#fff',
    color: colors.stone800,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    border: `1px solid ${colors.stone200}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.02)',
    marginBottom: 16,
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '12px 16px',
    borderBottom: `1px solid ${colors.stone100}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 12,
  },
  statCard: {
    background: colors.stone800,
    borderRadius: 12,
    padding: 14,
    border: `1px solid ${colors.stone700}`,
  },
  statTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: colors.stone300,
    marginBottom: 10,
  },
  segBar: {
    display: 'flex',
    height: 8,
    borderRadius: 4,
    overflow: 'hidden',
    background: colors.stone700,
  },
  legendGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 6,
    marginTop: 10,
  },
  legendItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 8px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    fontSize: 11,
    color: '#fff',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  legendItemActive: {
    border: '1px solid rgba(255,255,255,0.5)',
    background: 'rgba(255,255,255,0.1)',
  },
  searchWrapper: {
    position: 'relative',
    flex: 1,
    maxWidth: 280,
    minWidth: 180,
  },
  searchInput: {
    width: '100%',
    padding: '7px 10px 7px 10px',
    fontSize: 12,
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 8,
    background: '#fff',
    color: colors.stone800,
    outline: 'none',
  },
  select: {
    padding: '7px 26px 7px 10px',
    fontSize: 12,
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 8,
    background: '#fff',
    color: colors.stone700,
    cursor: 'pointer',
    outline: 'none',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2378716c' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
  },
  ...tableStyles,
  btnPrimary: {
    padding: '7px 12px',
    fontSize: 12,
    fontWeight: 600,
    color: '#fff',
    background: `linear-gradient(135deg, ${colors.accent500} 0%, ${colors.accent600} 100%)`,
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    boxShadow: '0 2px 6px rgba(6, 182, 212, 0.25)',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  pageBtn: {
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 6,
    color: colors.stone600,
    cursor: 'pointer',
  },
  toast: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    padding: '10px 16px',
    background: colors.stone800,
    color: '#fff',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 500,
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    animation: 'slideUp 0.3s ease',
  },
  modal: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(28, 25, 23, 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  modalContent: {
    background: '#fff',
    borderRadius: 16,
    width: '100%',
    maxWidth: 520,
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    padding: '16px 20px',
    borderBottom: `1px solid ${colors.stone200}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalBody: {
    padding: '16px 20px',
  },
  modalFooter: {
    padding: '12px 20px',
    borderTop: `1px solid ${colors.stone100}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  drawer: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(28, 25, 23, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'flex-end',
    zIndex: 1000,
  },
  drawerContent: {
    width: '60%',
    maxWidth: 900,
    height: '100%',
    background: '#fff',
    boxShadow: '-8px 0 32px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
  },
  formGroup: {
    marginBottom: 12,
  },
  label: {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    color: colors.stone500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 4,
  },
  input: {
    width: '100%',
    padding: '8px 10px',
    fontSize: 12,
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 8,
    background: colors.stone50,
    color: colors.stone800,
    outline: 'none',
  },
};

const getStatusDotColor = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'PENDING') return matte.amber;
  if (s === 'ACCEPTED') return matte.teal;
  if (s === 'IN_PROGRESS') return matte.purple;
  if (s === 'COMPLETED') return matte.blue;
  if (s === 'PAID') return matte.green;
  if (s === 'REJECTED') return matte.red;
  return matte.grey;
};

const getTaskStatusColors = (status) => {
  return { color: colors.stone700 };
};

export default function Orders() {
  const { company, user } = useAuth();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [orders, setOrders] = useState([]);
  const [quoteAdvancesMap, setQuoteAdvancesMap] = useState({});
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState('');
  const [loading, setLoading] = useState(false);
  const [slugDraft, setSlugDraft] = useState({});
  const [slugError, setSlugError] = useState({});
  const [activeTab, setActiveTab] = useState('orders');
  const [actionLoading, setActionLoading] = useState({});
  const [waModal, setWaModal] = useState({ open: false, id: null, phone: '', message: '' });
  const [payModal, setPayModal] = useState({ open: false, id: null, orderId: null });
  const [taskModal, setTaskModal] = useState({ open: false, orderId: null, initialTaskData: null });
  const [isTaskSubmitting, setIsTaskSubmitting] = useState(false);
  const [taskDetailModal, setTaskDetailModal] = useState({ open: false, loading: false, task: null, error: '' });
  const [showFullTaskDesc, setShowFullTaskDesc] = useState(false);
  const [taskStatusMap, setTaskStatusMap] = useState({});
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [supportingDataLoading, setSupportingDataLoading] = useState(false);
  const [showStats, setShowStats] = useState(true);
  const [orderDateRange, setOrderDateRange] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const pFromDate = params.get('fromDate');
    const pToDate = params.get('toDate');
    if (pFromDate && pToDate) return { startDate: pFromDate, endDate: pToDate, label: 'Custom' };
    const { start, end } = getCurrentFiscalYearRange();
    return { startDate: start.toISOString(), endDate: end.toISOString(), label: 'Current FY' };
  });
  const [pdfPreview, setPdfPreview] = useState({ open: false, url: '', label: '', loading: false, kind: '' });
  const [customerDrawer, setCustomerDrawer] = useState({ open: false, id: null, customer: null });
  const [orderDrawer, setOrderDrawer] = useState({ open: false, id: null });

  // Fix for default marker icons in Leaflet
  try {
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
    });
  } catch {}

  const linkifyText = (text) => {
    if (!text) return '';
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = String(text).split(urlRegex);
    return parts.map((part, idx) => urlRegex.test(part) ? <a key={idx} href={part} target="_blank" rel="noreferrer" style={{ color: colors.blue600 }}>{part}</a> : <span key={idx}>{part}</span>);
  };

  const isInvoicePaid = (invoice) => {
    const s = String(invoice?.status || '').toUpperCase();
    return s === 'PAID' || invoice?.isPaid === true || String(invoice?.paymentStatus || '').toUpperCase() === 'PAID';
  };

  useEffect(() => {
    const allIds = (orders || []).flatMap(o => Array.isArray(o.taskIds) ? o.taskIds : []);
    const missing = allIds.filter((id) => taskStatusMap[id] == null);
    if (missing.length === 0) return;
    const fetchAll = async () => {
      try {
        const results = await Promise.all(missing.map((id) => api.get(`/api/tasks/${id}`).catch(() => null)));
        const next = { ...taskStatusMap };
        results.forEach((resp, idx) => {
          const task = resp?.data?.task || resp?.data;
          if (task?.status) next[missing[idx]] = task.status;
        });
        setTaskStatusMap(next);
      } catch {}
    };
    fetchAll();
  }, [orders]);

  useEffect(() => {
    const quoteIds = Array.from(
      new Set(
        (orders || [])
          .map((o) => o?.quote?.id || o?.quoteId)
          .filter(Boolean)
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id))
      )
    );
    if (!quoteIds.length) return;
    const missing = quoteIds.filter((id) => quoteAdvancesMap[id] == null);
    if (!missing.length) return;
    const fetchAll = async () => {
      try {
        const results = await Promise.all(
          missing.map((id) =>
            api
              .get(`/api/quotes/${id}/advances`)
              .then((resp) => ({ id, data: resp?.data }))
              .catch(() => ({ id, data: null }))
          )
        );
        setQuoteAdvancesMap((prev) => {
          const next = { ...prev };
          results.forEach(({ id, data }) => {
            // Store a stable shape even on failure to prevent refetch loops
            next[id] = data || { received: 0, remaining: null, proformas: [] };
          });
          return next;
        });
      } catch {}
    };
    fetchAll();
  }, [orders, quoteAdvancesMap]);

  useEffect(() => {
    const load = async () => {
      if (!company?.id) return;
      setLoading(true);
      try {
        const { data } = await OrderTemplatesAPI.list({ companyId: company.id, pageSize: 200 });
        setTemplates(Array.isArray(data) ? data : (data?.data || []));
        const ordParams = { companyId: company.id, pageSize: 200 };
        if (orderDateRange?.startDate && orderDateRange?.endDate) {
          ordParams.startDate = orderDateRange.startDate;
          ordParams.endDate = orderDateRange.endDate;
        }
        const ord = await OrdersAPI.list(ordParams);
        setOrders(Array.isArray(ord?.data) ? ord.data : (ord?.data || []));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [company?.id, orderDateRange]);

  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(''), 3000); return () => clearTimeout(t); }, [toast]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = Array.isArray(templates?.data) ? templates.data : templates;
    if (!q) return list;
    return list.filter(t => (t.name || '').toLowerCase().includes(q));
  }, [templates, search]);

  const ordersFiltered = useMemo(() => {
    const list = Array.isArray(orders) ? orders : [];
    if (!orderDateRange?.startDate || !orderDateRange?.endDate) return list;
    try {
      const start = new Date(orderDateRange.startDate);
      const end = new Date(orderDateRange.endDate);
      return list.filter(o => {
        const d = o.createdAt ? new Date(o.createdAt) : null;
        return d && !Number.isNaN(d.getTime()) && d >= start && d <= end;
      });
    } catch { return list; }
  }, [orders, orderDateRange]);

  const handleOrderDateChange = (rangeOrAll) => setOrderDateRange(rangeOrAll === 'all' ? null : rangeOrAll);
  const openNew = () => navigate('/order/new');

  const loadSupportingData = async () => {
    if (!company?.id) return;
    setSupportingDataLoading(true);
    try {
      const [deps, emps, custs] = await Promise.all([
        api.get(`/api/departments/${company.id}`),
        api.get(`/api/companies/${company.id}/employees`),
        api.get(`/api/companies/${company.id}/customers`)
      ]);
      if (deps.data?.is_success) setDepartments(deps.data.departments || []);
      if (emps.data?.is_success) setEmployees(emps.data.employees || []);
      if (custs.data?.is_success) setCustomers(custs.data.customers || []);
    } catch {} finally { setSupportingDataLoading(false); }
  };

  const acceptOrder = async (orderId) => {
    try { await OrdersAPI.accept(orderId); setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: 'ACCEPTED' } : o)); setToast('Order accepted'); }
    catch (e) { setToast(e?.response?.data?.error || 'Failed to accept order'); }
  };

  const startOrder = async (orderId) => {
    try { await OrdersAPI.start(orderId); setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: 'IN_PROGRESS' } : o)); setToast('Order started'); }
    catch (e) { setToast(e?.response?.data?.error || 'Failed to start order'); }
  };

  const completeOrder = async (orderId) => {
    try { await OrdersAPI.complete(orderId); setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: 'COMPLETED' } : o)); setToast('Order completed'); }
    catch (e) { setToast(e?.response?.data?.error || 'Failed to complete order'); }
  };

  const rejectOrder = async (orderOrId) => {
    try {
      const orderId = typeof orderOrId === 'object' ? orderOrId.id : orderOrId;
      const linkedQuoteId = typeof orderOrId === 'object' ? orderOrId.quoteId : null;
      await OrdersAPI.reject(orderId);
      if (linkedQuoteId) try { await api.post(`/api/quotes/${linkedQuoteId}/status`, { status: 'REJECTED' }); } catch {}
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: 'REJECTED' } : o));
      setToast('Order rejected');
    } catch (e) { setToast(e?.response?.data?.error || 'Failed to reject order'); }
  };

  const handleDownloadPdf = async (id, numberOrLabel) => {
    const key = `pdf:${id}`;
    setActionLoading(s => ({ ...s, [key]: true }));
    try {
      const { data } = await api.get(`/api/invoices/${id}/pdf-url`);
      if (!data?.url) throw new Error('No URL');
      const response = await fetch(data.url);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${String(numberOrLabel || `INV${id}`).replace(/[^a-zA-Z0-9._-]+/g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
      setToast('Download started');
    } catch { setToast('Failed to download PDF'); }
    finally { setActionLoading(s => { const n = { ...s }; delete n[key]; return n; }); }
  };

  const openPdfPreview = async (kind, id, label) => {
    try {
      setPdfPreview({ open: true, url: '', label: label || '', loading: true, kind });
      const endpoint = kind === 'Quote' ? `/api/quotes/${id}/pdf-url` : `/api/invoices/${id}/pdf-url`;
      const { data } = await api.get(endpoint);
      if (!data?.url) throw new Error('No URL');
      setPdfPreview(p => ({ ...p, url: data.url, loading: false }));
    } catch { setToast('Failed to load PDF'); setPdfPreview({ open: false, url: '', label: '', loading: false, kind: '' }); }
  };
  const closePdfPreview = () => setPdfPreview({ open: false, url: '', label: '', loading: false, kind: '' });

  const openCreateTaskModal = async (o) => {
    try {
      if (!departments.length || !employees.length || !customers.length) await loadSupportingData();
      const parts = [`Task for Order #${o.id}`, `Order: ${window.location.origin}/orders/${o.id}`];
      const invoiceLabel = o?.invoice?.number || (o.invoiceId ? `INV${o.invoiceId}` : null);
      if (invoiceLabel) parts.push(`Invoice: ${invoiceLabel}`);
      const sa = o?.shippingAddress || {};
      const loc = sa?.location || {};
      const addressParts = [sa.addressLine1, sa.addressLine2, sa.city, sa.state, sa.pincode].filter(Boolean);
      const initialTaskData = {
        description: parts.join('\n'),
        customerId: o.customer?.id || '',
        location: { coordinates: { lat: loc.lat ?? null, lng: loc.lng ?? null }, address: sa.resolvedAddress || addressParts.join(', ') || '' },
        images: []
      };
      setTaskModal({ open: true, orderId: o.id, initialTaskData });
    } catch { setToast('Failed to open Create Task'); }
  };
  const closeCreateTaskModal = () => setTaskModal({ open: false, orderId: null, initialTaskData: null });

  const handleCreateTaskSubmit = async (taskData) => {
    try {
      if (!company?.id) return { success: false, error: 'No company' };
      setIsTaskSubmitting(true);
      const payload = {
        companyId: company.id,
        description: taskData.description,
        departmentId: taskData.departmentId || null,
        location: taskData.location || null,
        images: taskData.images || [],
        customer: taskData.customer,
        dueDate: taskData.dueDate?.trim() ? new Date(taskData.dueDate).toISOString() : null,
        followUpOn: taskData.followUpOn?.trim() ? new Date(taskData.followUpOn).toISOString() : null,
        employeeId: taskData.employeeId || null,
        supervisorId: taskData.supervisorId || null
      };
      const response = await api.post('/api/companies/complaints/create-on-behalf', payload);
      if (response.data?.is_success) {
        const created = response.data.complaint;
        if (taskModal.orderId) {
          try {
            const res = await OrdersAPI.addTask(taskModal.orderId, created.id);
            const nextTaskIds = Array.isArray(res?.taskIds) ? res.taskIds : [created.id];
            setOrders(prev => prev.map(or => or.id === taskModal.orderId ? { ...or, taskIds: nextTaskIds } : or));
          } catch {
            setOrders(prev => prev.map(or => or.id === taskModal.orderId ? { ...or, taskIds: [...(or.taskIds || []), created.id] } : or));
          }
        }
        setToast('Task created');
        return { success: true, task: created };
      }
      return { success: false, error: response.data?.message || 'Failed to create task' };
    } catch { return { success: false, error: 'Failed to create task' }; }
    finally { setIsTaskSubmitting(false); }
  };

  const openTaskDetail = async (taskId) => {
    try {
      setShowFullTaskDesc(false);
      setTaskDetailModal({ open: true, loading: true, task: null, error: '' });
      const { data } = await api.get(`/api/tasks/${taskId}`);
      if (data?.is_success === false && data?.message) { setTaskDetailModal({ open: true, loading: false, task: null, error: data.message }); return; }
      const task = data?.task || data;
      setTaskDetailModal({ open: true, loading: false, task, error: '' });
      if (task?.id && task?.status) setTaskStatusMap(m => ({ ...m, [task.id]: task.status }));
    } catch (e) { setTaskDetailModal({ open: true, loading: false, task: null, error: e?.response?.data?.message || 'Failed to load task' }); }
  };
  const closeTaskDetail = () => { setTaskDetailModal({ open: false, loading: false, task: null, error: '' }); setShowFullTaskDesc(false); };

  const openWhatsAppModal = async (o) => {
    const phone = String(o.customer?.phone || o.customer?.mobile || o.customer?.whatsapp || user?.phone || '');
    let message = `Hello ${o.customer?.name || ''}, here is Invoice INV${o.invoiceId} for ${formatINR(o?.totals?.total || 0)}.`;
    try { const { data } = await api.get(`/api/invoices/${o.invoiceId}/pdf-url`); if (data?.url) message += `\nPDF: ${data.url}`; } catch {}
    message += `\nThank you!`;
    setWaModal({ open: true, id: o.invoiceId, phone, message });
  };
  const closeWhatsAppModal = () => setWaModal({ open: false, id: null, phone: '', message: '' });
  const handleSendWhatsApp = () => {
    const { phone, message } = waModal;
    const digits = phone.replace(/\D/g, '');
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(message)}`, '_blank');
    setToast('Opening WhatsApp…');
    closeWhatsAppModal();
  };

  const openPayModal = (o) => setPayModal({ open: true, id: o.invoiceId || null, orderId: o.id });
  const closePayModal = () => setPayModal({ open: false, id: null, orderId: null });

  const handleSubmitMarkPaid = async (paymentData) => {
    const { id, orderId } = payModal;
    try {
      setActionLoading(s => ({ ...s, [`paid:${orderId}`]: true }));
      if (id) await api.post(`/api/invoices/${id}/mark-paid`, paymentData);
      const res = await OrdersAPI.markPaid(orderId, paymentData);
      setToast('Payment recorded');
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: 'PAID', invoiceId: res?.invoiceId || o.invoiceId } : o));
      try { const fresh = await OrdersAPI.get(orderId); setOrders(prev => prev.map(o => o.id === orderId ? fresh : o)); } catch {}
      closePayModal();
    } catch { setToast('Failed to mark as paid'); }
    finally { setActionLoading(s => { const n = { ...s }; delete n[`paid:${orderId}`]; return n; }); }
  };

  const openCustomerDetail = (customer) => { if (customer?.id) setCustomerDrawer({ open: true, id: customer.id, customer }); };
  const openOrderDetail = (id) => { if (id) setOrderDrawer({ open: true, id }); };
  const closeCustomerDrawer = () => setCustomerDrawer({ open: false, id: null, customer: null });
  const closeOrderDrawer = () => setOrderDrawer({ open: false, id: null });

  const openEdit = (tpl) => navigate(`/order/${tpl.id}/edit`);

  const duplicate = async (tpl) => {
    try {
      const { id, createdAt, updatedAt, deletedAt, version, ...rest } = tpl || {};
      const created = await OrderTemplatesAPI.create({ ...rest, name: `${tpl.name} (Copy)`, version: 1, publicSlug: null });
      setTemplates(prev => [created, ...(Array.isArray(prev?.data) ? prev.data : prev)]);
      setToast('Template duplicated');
    } catch {}
  };

  const slugify = (val) => String(val || '').toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').slice(0, 200);

  const saveSlug = async (tpl, rawVal) => {
    const clean = slugify(rawVal);
    try {
      const updated = await OrderTemplatesAPI.update(tpl.id, { publicSlug: clean || null });
      setTemplates(prev => (Array.isArray(prev?.data) ? prev.data : prev).map(t => t.id === tpl.id ? { ...t, publicSlug: updated.publicSlug } : t));
      setSlugDraft(s => ({ ...s, [tpl.id]: updated.publicSlug || '' }));
      setSlugError(s => { const next = { ...s }; delete next[tpl.id]; return next; });
      setToast('Slug saved');
    } catch (e) {
      const msg = e?.response?.status === 409 ? 'Slug already exists' : (e?.response?.data?.error || 'Failed to save');
      setSlugError(s => ({ ...s, [tpl.id]: msg }));
    }
  };

  const remove = async (id) => {
    try { await OrderTemplatesAPI.remove(id); setTemplates(prev => (Array.isArray(prev?.data) ? prev.data : prev).filter(t => t.id !== id)); setToast('Template deleted'); }
    catch {}
  };

  const totalFor = (tpl) => {
    try {
      const groups = Array.isArray(tpl.itemGroups) ? tpl.itemGroups : [];
      return groups.flatMap(g => g.lines || []).reduce((acc, l) => acc + (Number((l.minQty ?? l.quantity) || 0) * Number(l.price || 0)), 0);
    } catch { return 0; }
  };

  const formatINR = (val) => {
    try { return '₹' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(Number(val || 0)); } catch { return val; }
  };

  const formatDateTime = (val) => {
    if (!val) return '-';
    try { return new Date(val).toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: 'numeric', minute: '2-digit', hour12: true }); } catch { return '-'; }
  };

  const formatInvoiceShort = (value) => {
    const raw = String(value || '');
    const digits = raw.replace(/\D/g, '').slice(-6);
    return digits ? `..${digits}` : raw;
  };

  const formatStatus = (value) => value ? String(value).replace(/_/g, ' ') : '-';

  const orderStats = useMemo(() => {
    const initial = { pending: 0, accepted: 0, inProgress: 0, completed: 0, paid: 0, rejected: 0, amountDue: 0, amountReceived: 0 };
    return (ordersFiltered || []).reduce((acc, o) => {
      const status = String(o.status || '').toUpperCase();
      if (status === 'PENDING') acc.pending += 1;
      else if (status === 'ACCEPTED') acc.accepted += 1;
      else if (status === 'IN_PROGRESS') acc.inProgress += 1;
      else if (status === 'COMPLETED') acc.completed += 1;
      else if (status === 'PAID') acc.paid += 1;
      else if (status === 'REJECTED') acc.rejected += 1;

      const total = Number(o?.invoice?.total || o?.totals?.total || 0);
      const isPaid = isInvoicePaid(o?.invoice) || status === 'PAID';
      if (!(total > 0) || status === 'REJECTED') return acc;

      // Advances are recorded as proformas linked to the quote.
      // For unpaid orders, treat advance received as "Amount Received" and reduce "Amount Due".
      // For paid orders, count the full total as received (avoid double-counting advances).
      if (isPaid) {
        acc.amountReceived += total;
        return acc;
      }

      const qid = Number(o?.quote?.id || o?.quoteId || 0) || null;
      const advReceived = qid ? Number(quoteAdvancesMap[qid]?.received || 0) : 0;
      const cappedReceived = Math.max(0, Math.min(advReceived, total));
      const due = Math.max(0, total - cappedReceived);

      acc.amountReceived += cappedReceived;
      acc.amountDue += due;
      return acc;
    }, initial);
  }, [ordersFiltered, quoteAdvancesMap]);

  const renderPrimaryAction = (o) => {
    const s = String(o.status || '').toUpperCase();
    switch (s) {
      case 'PENDING':
        return (
          <>
            <button style={{ ...styles.btnIcon, color: colors.emerald600 }} onClick={() => acceptOrder(o.id)} title="Accept"><FiCheckCircle size={16} /></button>
            <button style={{ ...styles.btnIcon, color: colors.rose600 }} onClick={() => rejectOrder(o)} title="Reject"><FiXCircle size={16} /></button>
          </>
        );
      case 'ACCEPTED':
        return <button style={{ ...styles.btnIcon, color: colors.blue600 }} onClick={() => startOrder(o.id)} title="Start"><FiPlay size={16} /></button>;
      case 'IN_PROGRESS':
        return <button style={{ ...styles.btnIcon, color: colors.emerald600 }} onClick={() => completeOrder(o.id)} title="Complete"><FiCheckCircle size={16} /></button>;
      case 'COMPLETED':
        return <button style={{ ...styles.btnIcon, color: colors.accent600 }} onClick={() => openPayModal(o)} title="Mark Paid"><FiCreditCard size={16} /></button>;
      case 'PAID':
        return <span style={{ fontSize: 10, color: colors.emerald600, fontWeight: 600 }}>PAID</span>;
      case 'REJECTED':
        return <span style={{ fontSize: 10, color: colors.stone400, fontWeight: 600 }}>REJECTED</span>;
      default:
        return null;
    }
  };

  const SegmentBar = ({ segments }) => {
    const sum = segments.reduce((a, s) => a + Number(s.value || 0), 0) || 1;
    return (
      <div style={styles.segBar}>
        {segments.map((s, i) => <div key={i} title={s.title} style={{ width: `${(Number(s.value || 0) / sum) * 100}%`, background: s.color }} />)}
      </div>
    );
  };

  const LegendItem = ({ label, value, color }) => (
    <div style={styles.legendItem}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
        {label}
      </span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );

  return (
    <div style={styles.page}>
      <style>{billingTableCSS()}</style>

      {/* Toast */}
      {!!toast && (
        <div style={styles.toast}>
          <FiCheckCircle size={14} />
          <span>{toast}</span>
          <button onClick={() => setToast('')} style={{ ...styles.btnIcon, color: '#fff' }}><FiX size={14} /></button>
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div>
            <h1 style={styles.title}>Orders</h1>
            <p style={styles.subtitle}>{ordersFiltered.length} orders • {filtered.length} stores</p>
          </div>
        </div>
        <div style={styles.headerActions}>
          {activeTab === 'orders' && <HeaderDateFilters value={orderDateRange} onChange={handleOrderDateChange} />}
          {activeTab === 'templates' && (
            <button
              style={styles.btnSecondary}
              onClick={openNew}
            >
              <FiPlus size={14} /> New Store
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabBar}>
        <button style={{ ...styles.tab, ...(activeTab === 'orders' ? styles.tabActive : {}) }} onClick={() => setActiveTab('orders')}>Orders Received</button>
        <button style={{ ...styles.tab, ...(activeTab === 'templates' ? styles.tabActive : {}) }} onClick={() => setActiveTab('templates')}>Setup Store</button>
      </div>

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <>
          {/* Stats */}
          {showStats && (
            <div style={{ ...styles.card, background: colors.stone800, border: `1px solid ${colors.stone700}` }}>
              <div style={{ padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>Overview</span>
                  <button style={{ ...styles.btnIcon, color: colors.stone400 }} onClick={() => setShowStats(false)} title="Hide Stats"><FiEyeOff size={14} /></button>
                </div>
                <div style={styles.statsGrid}>
                  {/* Status Card */}
                  <div style={styles.statCard}>
                    <div style={styles.statTitle}>Status</div>
                    <SegmentBar segments={[
                      { value: orderStats.pending, color: matte.amber, title: 'Pending' },
                      { value: orderStats.accepted, color: matte.teal, title: 'Accepted' },
                      { value: orderStats.inProgress, color: matte.purple, title: 'In Progress' },
                      { value: orderStats.completed, color: matte.blue, title: 'Completed' },
                      { value: orderStats.paid, color: matte.green, title: 'Paid' },
                      { value: orderStats.rejected, color: matte.red, title: 'Rejected' },
                    ]} />
                    <div style={styles.legendGrid}>
                      <LegendItem label="Pending" value={orderStats.pending} color={matte.amber} />
                      <LegendItem label="Accepted" value={orderStats.accepted} color={matte.teal} />
                      <LegendItem label="In Progress" value={orderStats.inProgress} color={matte.purple} />
                      <LegendItem label="Completed" value={orderStats.completed} color={matte.blue} />
                      <LegendItem label="Paid" value={orderStats.paid} color={matte.green} />
                      <LegendItem label="Rejected" value={orderStats.rejected} color={matte.red} />
                    </div>
                  </div>
                  {/* Amounts Card */}
                  <div style={styles.statCard}>
                    <div style={styles.statTitle}>Amounts</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 8 }}>
                      <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: 12 }}>
                        <div style={{ fontSize: 11, color: colors.stone400 }}>Amount Due</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: matte.amber, marginTop: 4 }}>{formatINR(orderStats.amountDue)}</div>
                      </div>
                      <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: 12 }}>
                        <div style={{ fontSize: 11, color: colors.stone400 }}>Amount Received</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: matte.green, marginTop: 4 }}>{formatINR(orderStats.amountReceived)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {!showStats && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <button style={styles.btnSecondary} onClick={() => setShowStats(true)}><FiChevronDown size={14} /> Show Stats</button>
            </div>
          )}

          {/* Orders Table */}
          <div style={styles.card}>
            <div style={styles.tableWrap}>
              <table className="billing-table" style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Order</th>
                    <th style={styles.th}>Customer</th>
                    <th style={styles.th}>Quote</th>
                    <th style={styles.th}>Invoice</th>
                    <th style={styles.th}>Tasks</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Created</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Total</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center' }}>
                      <div style={{ display: 'inline-block', width: 20, height: 20, border: `2px solid ${colors.stone200}`, borderTopColor: colors.accent500, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    </td></tr>
                  )}
                  {!loading && !ordersFiltered.length && (
                    <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: colors.stone400, fontSize: 12 }}>No orders found</td></tr>
                  )}
                  {!loading && ordersFiltered.map(o => {
                    const statusDot = getStatusDotColor(o.status);
                    return (
                      <tr key={o.id} className="billing-row" style={{ transition: 'background 0.15s' }}>
                        <td style={{ ...styles.td, fontWeight: 600 }}>
                          <button style={styles.linkBtn} onClick={() => openOrderDetail(o.id)}>{o.number || `ORD${o.id}`}</button>
                        </td>
                        <td style={{ ...styles.td, fontWeight: 600 }}>
                          {o.customer?.id ? (
                            <button style={styles.linkBtn} onClick={() => openCustomerDetail(o.customer)}>{o.customer?.name || `Customer ${o.customer.id}`}</button>
                          ) : (o.customer?.name || <span style={{ color: colors.stone400 }}>-</span>)}
                        </td>
                        <td style={{ ...styles.td, fontSize: 12 }}>
                          {(o.quote?.id || o.quoteId) ? (
                            <button style={styles.linkBtn} onClick={() => openPdfPreview('Quote', o.quote?.id || o.quoteId, o.quote?.number || `QUOTE-${o.quote?.id || o.quoteId}`)}>
                              {formatInvoiceShort(o.quote?.number || `QUOTE-${o.quote?.id || o.quoteId}`)}
                            </button>
                          ) : <span style={{ color: colors.stone400 }}>-</span>}
                        </td>
                        <td style={{ ...styles.td, fontSize: 12 }}>
                          {o.invoiceId ? (
                            <button style={{ ...styles.linkBtn, color: colors.emerald600 }} onClick={() => openPdfPreview('Invoice', o.invoiceId, o?.invoice?.number || `INV${o.invoiceId}`)}>
                              {formatInvoiceShort(o?.invoice?.number || `INV${o.invoiceId}`)}
                            </button>
                          ) : <span style={{ color: colors.stone400 }}>-</span>}
                        </td>
                        <td style={{ ...styles.td, fontSize: 12 }}>
                          {Array.isArray(o.taskIds) && o.taskIds.length > 0 ? (
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              {o.taskIds.map(tid => {
                                const color = getTaskStatusColors(taskStatusMap[tid]);
                                return <button key={tid} style={{ ...styles.linkBtn, ...color }} onClick={() => openTaskDetail(tid)}>{tid}</button>;
                              })}
                            </div>
                          ) : <span style={{ color: colors.stone400 }}>-</span>}
                        </td>
                        <td style={styles.td}>
                          <span style={styles.statusWithDot}>
                            <span style={styles.statusDot(statusDot)} />
                            {formatStatus(o.status)}
                          </span>
                        </td>
                        <td style={{ ...styles.td, whiteSpace: 'nowrap', fontSize: 12, color: colors.stone500 }}>{formatDateTime(o.createdAt)}</td>
                        <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600, color: colors.stone800, fontFeatureSettings: '"tnum"' }}>
                          {(() => {
                            const total = Number(o?.totals?.total || 0);
                            const qid = Number(o?.quote?.id || o?.quoteId || 0) || null;
                            const adv = qid ? quoteAdvancesMap[qid] : null;
                            const received = Number(adv?.received || 0);
                            const remainingRaw = adv?.remaining;
                            const remaining = remainingRaw == null ? Math.max(0, total - received) : Math.max(0, Number(remainingRaw || 0));
                            const hasInvoice = !!(o?.invoiceId || o?.invoice?.id);
                            const isPaid = hasInvoice || isInvoicePaid(o?.invoice) || String(o?.status || '').toUpperCase() === 'PAID';
                            const showPending = !isPaid && qid && received > 0 && remaining > 0;
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                                <div style={{ fontWeight: 600 }}>{formatINR(total)}</div>
                                {showPending && (
                                  <div style={{ fontSize: 10, fontWeight: 600, color: colors.amber700 }}>
                                    Pending: {formatINR(remaining)}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                            {renderPrimaryAction(o)}
                            {!['PAID', 'REJECTED'].includes(String(o.status || '').toUpperCase()) && o.quoteId && (
                              <button style={styles.btnIcon} onClick={() => navigate(`/quote/${o.quoteId}/edit`)} title="Edit Quote"><FiEdit2 size={14} /></button>
                            )}
                            <button style={styles.btnIcon} onClick={() => openOrderDetail(o.id)} title="View Order"><FiPrinter size={14} /></button>
                            <button style={styles.btnIcon} onClick={() => openCreateTaskModal(o)} title="Create Task"><FiPlus size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <>
          {/* Search */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <input style={{ ...styles.searchInput, maxWidth: 280 }} placeholder="Search templates..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>

          {/* Templates Table */}
          <div style={styles.card}>
            <div style={styles.tableWrap}>
              <table className="billing-table" style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Items</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Est. Total</th>
                    <th style={styles.th}>Public URL</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center' }}>
                      <div style={{ display: 'inline-block', width: 20, height: 20, border: `2px solid ${colors.stone200}`, borderTopColor: colors.accent500, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    </td></tr>
                  )}
                  {!loading && !filtered.length && (
                    <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: colors.stone400, fontSize: 12 }}>No templates yet</td></tr>
                  )}
                  {!loading && filtered.map(tpl => {
                    const groups = Array.isArray(tpl.itemGroups) ? tpl.itemGroups : [];
                    const linesCount = groups.reduce((n, g) => n + (g.lines?.length || 0), 0);
                    const companySlug = slugify(company?.name || company?.legalName || 'company');
                    const templateSlug = tpl.publicSlug || slugify(tpl.name) || String(tpl.id);
                    const publicPath = `/${companySlug}/${templateSlug}`;
                    return (
                      <tr key={tpl.id} className="billing-row" style={{ transition: 'background 0.15s' }}>
                        <td style={{ ...styles.td, fontWeight: 600 }}>
                          <div style={{ color: colors.stone800 }}>{tpl.name}</div>
                          {!!tpl.notes && <div style={{ color: colors.stone500, fontSize: 11, fontWeight: 400 }}>{tpl.notes}</div>}
                        </td>
                        <td style={{ ...styles.td, fontSize: 12 }}>{linesCount}</td>
                        <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600, color: colors.stone800, fontFeatureSettings: '"tnum"' }}>{formatINR(totalFor(tpl))}</td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ color: colors.stone400, fontFamily: 'monospace', fontSize: 11 }}>/{companySlug}/</span>
                            <input
                              style={{ ...styles.input, width: 120, padding: '4px 6px', fontSize: 11 }}
                              placeholder={slugify(tpl.name) || 'template-slug'}
                              value={slugDraft[tpl.id] ?? (tpl.publicSlug || '')}
                              onChange={e => setSlugDraft(s => ({ ...s, [tpl.id]: e.target.value }))}
                              onBlur={e => saveSlug(tpl, e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setSlugDraft(s => ({ ...s, [tpl.id]: tpl.publicSlug || '' })); }}
                            />
                            {!!tpl.publicSlug && (
                              <>
                                <a href={publicPath} target="_blank" rel="noreferrer" style={{ ...styles.btnIcon, color: colors.blue600 }} title="Preview"><FiEye size={14} /></a>
                                <button style={styles.btnIcon} onClick={() => { navigator.clipboard.writeText(window.location.origin + publicPath); setToast('Link copied'); }} title="Copy link"><FiClipboard size={14} /></button>
                              </>
                            )}
                          </div>
                          {!!slugError[tpl.id] && <div style={{ color: colors.rose600, fontSize: 11, marginTop: 4 }}>{slugError[tpl.id]}</div>}
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                            <button style={styles.btnIcon} onClick={() => openEdit(tpl)} title="Edit"><FiEdit2 size={14} /></button>
                            <button style={styles.btnIcon} onClick={() => duplicate(tpl)} title="Duplicate"><FiCopy size={14} /></button>
                            <button style={{ ...styles.btnIcon, color: colors.rose600 }} onClick={() => remove(tpl.id)} title="Delete"><FiTrash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* PDF Preview Drawer */}
      {pdfPreview.open && (
        <div style={styles.drawer} onClick={closePdfPreview}>
          <div style={styles.drawerContent} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '14px 20px', borderBottom: `1px solid ${colors.stone200}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>{pdfPreview.kind} - {pdfPreview.label}</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                {!!pdfPreview.url && <a href={pdfPreview.url} target="_blank" rel="noreferrer" style={{ ...styles.btnSecondary, textDecoration: 'none' }}><FiExternalLink size={14} /> Open</a>}
                <button style={styles.btnSecondary} onClick={closePdfPreview}>Close</button>
              </div>
            </div>
            <div style={{ flex: 1, background: colors.stone100 }}>
              {pdfPreview.loading ? (
                <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: 24, height: 24, border: `2px solid ${colors.stone200}`, borderTopColor: colors.accent500, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                </div>
              ) : pdfPreview.url ? (
                <iframe title="PDF" src={pdfPreview.url} style={{ width: '100%', height: '100%', border: 'none' }} />
              ) : <div style={{ padding: 20, color: colors.stone500 }}>No preview</div>}
            </div>
          </div>
        </div>
      )}

      {/* Customer Drawer */}
      {customerDrawer.open && (
        <div style={styles.drawer} onClick={closeCustomerDrawer}>
          <div style={{ ...styles.drawerContent, width: '70%', maxWidth: 1000 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '14px 20px', borderBottom: `1px solid ${colors.stone200}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>Customer</h3>
              <button style={styles.btnSecondary} onClick={closeCustomerDrawer}>Close</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
              <CustomerDetail embeddedCustomerId={customerDrawer.id} embeddedCustomer={customerDrawer.customer} />
            </div>
          </div>
        </div>
      )}

      {/* Order Drawer */}
      {orderDrawer.open && (
        <div style={styles.drawer} onClick={closeOrderDrawer}>
          <div style={{ ...styles.drawerContent, width: '70%', maxWidth: 1000 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '14px 20px', borderBottom: `1px solid ${colors.stone200}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>Order</h3>
              <button style={styles.btnSecondary} onClick={closeOrderDrawer}>Close</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
              <OrderDetail embeddedOrderId={orderDrawer.id} onClose={closeOrderDrawer} />
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Modal */}
      {waModal.open && (
        <div style={styles.modal} onClick={closeWhatsAppModal}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Send via WhatsApp</h3>
              <button style={styles.btnIcon} onClick={closeWhatsAppModal}><FiX size={18} /></button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.formGroup}><label style={styles.label}>Phone</label><input style={styles.input} value={waModal.phone} onChange={e => setWaModal(m => ({ ...m, phone: e.target.value }))} placeholder="e.g. 9198xxxxxx" /></div>
              <div style={styles.formGroup}><label style={styles.label}>Message</label><textarea style={{ ...styles.input, minHeight: 100, resize: 'vertical' }} value={waModal.message} onChange={e => setWaModal(m => ({ ...m, message: e.target.value }))} /></div>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.btnSecondary} onClick={closeWhatsAppModal}>Cancel</button>
              <button style={styles.btnSecondary} onClick={handleSendWhatsApp}>Open WhatsApp</button>
            </div>
          </div>
        </div>
      )}

      {/* Pay Modal */}
      <PaymentModal
        isOpen={payModal.open}
        onClose={closePayModal}
        onSubmit={handleSubmitMarkPaid}
        title="Mark Invoice as Paid"
        submitText="Mark as Paid"
        submitLoadingText="Saving..."
        isSubmitting={!!actionLoading[`paid:${payModal.orderId}`]}
      />

      {/* Task Detail Modal */}
      {taskDetailModal.open && (
        <div style={styles.modal} onClick={closeTaskDetail}>
          <div style={{ ...styles.modalContent, maxWidth: 720 }} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Task Details</h3>
              <button style={styles.btnIcon} onClick={closeTaskDetail}><FiX size={18} /></button>
            </div>
            <div style={styles.modalBody}>
              {taskDetailModal.loading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
                  <div style={{ width: 24, height: 24, border: `2px solid ${colors.stone200}`, borderTopColor: colors.accent500, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                </div>
              )}
              {!!taskDetailModal.error && !taskDetailModal.loading && (
                <div style={{ background: colors.rose50, color: colors.rose700, padding: '10px 14px', borderRadius: 8, fontSize: 12 }}>{taskDetailModal.error}</div>
              )}
              {!taskDetailModal.loading && taskDetailModal.task && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={{ gridColumn: '1 / -1', background: colors.stone50, padding: 12, borderRadius: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 600, fontSize: 12 }}>#{taskDetailModal.task?.id}</span>
                      <span style={{ ...styles.badge, background: colors.emerald50, color: colors.emerald700 }}>{String(taskDetailModal.task?.status || '').toUpperCase()}</span>
                    </div>
                    <div style={{ marginTop: 8, color: colors.stone700, fontSize: 12, lineHeight: 1.5 }}>
                      {(() => {
                        const text = taskDetailModal.task?.description || '';
                        const shouldTruncate = !showFullTaskDesc && text.length > 300;
                        return linkifyText(shouldTruncate ? text.slice(0, 300) + '…' : text);
                      })()}
                    </div>
                    {(taskDetailModal.task?.description || '').length > 300 && (
                      <button style={{ ...styles.linkBtn, marginTop: 8, fontSize: 11 }} onClick={() => setShowFullTaskDesc(v => !v)}>
                        {showFullTaskDesc ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </div>
                  <div><div style={styles.label}>Priority</div><div style={{ fontSize: 12, color: colors.stone700 }}>{taskDetailModal.task?.priority || '-'}</div></div>
                  <div><div style={styles.label}>Due Date</div><div style={{ fontSize: 12, color: colors.stone700 }}>{taskDetailModal.task?.dueDate ? formatDateTime(taskDetailModal.task.dueDate) : '-'}</div></div>
                  <div><div style={styles.label}>Customer</div><div style={{ fontSize: 12, color: colors.stone700 }}>{taskDetailModal.task?.customer?.name || '-'}</div></div>
                  <div><div style={styles.label}>Supervisor</div><div style={{ fontSize: 12, color: colors.stone700 }}>{taskDetailModal.task?.supervisor ? `${taskDetailModal.task.supervisor.firstName || ''} ${taskDetailModal.task.supervisor.lastName || ''}`.trim() : '-'}</div></div>
                  <div><div style={styles.label}>Employee</div><div style={{ fontSize: 12, color: colors.stone700 }}>{taskDetailModal.task?.assignedEmployee ? `${taskDetailModal.task.assignedEmployee.firstName || ''} ${taskDetailModal.task.assignedEmployee.lastName || ''}`.trim() : '-'}</div></div>
                  <div><div style={styles.label}>Department</div><div style={{ fontSize: 12, color: colors.stone700 }}>{taskDetailModal.task?.department?.name || '-'}</div></div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={styles.label}>Location</div>
                    <div style={{ fontSize: 12, color: colors.stone700 }}>{taskDetailModal.task?.location?.address || '-'}</div>
                    {!!(taskDetailModal.task?.location?.coordinates) && (
                      <div style={{ color: colors.stone500, marginTop: 2, fontSize: 11 }}>
                        Lat: {taskDetailModal.task.location.coordinates.lat ?? '-'}, Lng: {taskDetailModal.task.location.coordinates.lng ?? '-'}
                      </div>
                    )}
                  </div>
                  {!!(taskDetailModal.task?.location?.coordinates?.lat) && !!(taskDetailModal.task?.location?.coordinates?.lng) && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div style={{ border: `1px solid ${colors.stone200}`, borderRadius: 8, overflow: 'hidden', position: 'relative' }}>
                        <MapContainer center={[taskDetailModal.task.location.coordinates.lat, taskDetailModal.task.location.coordinates.lng]} zoom={15} scrollWheelZoom={false} style={{ height: 200, width: '100%' }}>
                          <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                          <Marker position={[taskDetailModal.task.location.coordinates.lat, taskDetailModal.task.location.coordinates.lng]}>
                            <Popup>Task location</Popup>
                          </Marker>
                        </MapContainer>
                        <div style={{ position: 'absolute', left: 8, bottom: 8, right: 8, background: 'rgba(255,255,255,0.95)', padding: '6px 10px', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 11, color: colors.stone700 }}>{taskDetailModal.task.location.address || 'Unknown'}</span>
                          <a href={`https://www.google.com/maps?q=${taskDetailModal.task.location.coordinates.lat},${taskDetailModal.task.location.coordinates.lng}`} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: colors.blue600 }}>Open in Maps</a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={styles.modalFooter}><button style={styles.btnSecondary} onClick={closeTaskDetail}>Close</button></div>
          </div>
        </div>
      )}

      {/* Create Task Modal */}
      {taskModal.open && (
        <CreateTaskModal
          isOpen={taskModal.open}
          onClose={closeCreateTaskModal}
          onSubmit={handleCreateTaskSubmit}
          departments={departments}
          employees={employees}
          customers={customers}
          employeeLocations={[]}
          isSubmitting={isTaskSubmitting}
          initialTaskData={taskModal.initialTaskData}
        />
      )}
    </div>
  );
}
